import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useNotesContext, useRoomContext, useWhiteboardContext } from '@src/contexts';
import {
  useRoomActionsStore,
  useDrawerStore,
  usePollsStore,
  usePeersStore,
  useNotificationStore,
  useExitRoomStore,
  useMediaSettingsStore,
  useRoomStore,
  useClosedCaptionsStore,
  useRoomConfigStore,
} from '@src/stores';

import { isWebView } from '@src/helpers';
import { isIOS, isSamsungDevice } from '@src/utils';
import SocketService from '@src/services/SocketService';
import { useBeforeUnload, useEnumerateDevices, useMediaSettings } from '@src/hooks';

import { INFO_MODAL_ICONS } from '@src/constants';

import { ECommonSocketKeys, IPeer, ISocketCmd, TEmojiId, EUserType } from '@src/types';
import { useWebinarRoomStore } from '@src/stores/webinar';

const useRoomFooter = () => {
  const [isMicExtensionOpen, setIsMicExtensionOpen] = useState(false);
  const [isVideoExtensionOpen, setIsVideoExtensionOpen] = useState(false);
  const [trialLockCountdown, setTrialLockCountdown] = useState<number | null>(null);
  const micButtonRef = useRef<HTMLDivElement | null>(null);
  const camButtonRef = useRef<HTMLDivElement | null>(null);

  const {
    thereAreParticipants,
    hasActiveLimitedPolls,
    emitCmd,
    handleCmd,
    getCurrentPeer,
    handleUpdatePeerInfo,

    participantsPeers,
    isHostOrCohost,
    isPublicChatEnabled,
    isPrivateChatEnabled,
    isQuestionAndAnswerEnabled,
    isPollEnabled,
    canUserShareScreen,
    canUserEndMeeting,
    hasTempShareAccess,
    hasRequestedShare,
    isCallEnded,
    setHasRequestedShare,
    setHasUsedShareAccess,
    setIsCallEnded,
    micRequest,
    setMicRequest,
    camRequest,
    setCamRequest,

    currentPeerId,
    currentPeerType,
    isCurrentPeerSharing,
    shareScreenStream,
    hasChatNotifications,

    //buttons visibility
    isStartAudioButtonEnabled,
    isStartVideoButtonEnabled,
    isTranscriptionButtonEnabled,
    isEmojiRoomButtonEnabled,
    isShareButtonEnabled,
    isRaiseHandButtonEnabled,
    isExitButtonEnabled,
    isEditorButtonEnabled,
    isParticipantsButtonEnabled,
    isThreeDotsFooterMenuEnabled,
    currentPeer,
  } = useRoomContext();

  const { speakerDevices, audioInputDevices, videoInputDevices } = useMediaSettingsStore(
    useShallow((state) => ({
      speakerDevices: state.mediaSettings.speakerDevices,
      audioInputDevices: state.mediaSettings.audioInputDevices,
      videoInputDevices: state.mediaSettings.videoInputDevices,
    }))
  );
  const { selectedAudio, selectedSpeaker, selectedCamera, handleChangeMediaDevice } = useMediaSettings();

  const {
    micSettings: { isMicClosed, isMicButtonDisabled, onMicClick },
    camSettings: { isCamClosed, isCamButtonDisabled, onCamClick },
    raiseHandSettings: { isActive: isRaiseHandActive, isDisabled: isRaiseHandDisabled, onClick: onRaiseHandClick },
    shareScreenSettings: {
      isActive: isShareScreenActive,
      isDisabled: isShareScreenDisabled,
      isLoading: isShareScreenLoading,
      trialLockEndTime: shareScreenTrialLockEndTime,
      onClick: onShareScreenClick,
    },
    transcriptionSettings: {
      isActive: isTranscriptionActive,
      isDisabled: isTranscriptionDisabled,
      onClick: onTranscriptionClick,
    },
    exitRoomSettings: { onExitClick },
    whiteboardSettings,
    screenRecordingSettings: { isActive: isScreenRecordingActive, onClick: onRecordClick },
    setCamSettings,
  } = useRoomActionsStore(
    useShallow((state) => ({
      micSettings: state.micSettings,
      camSettings: state.camSettings,
      exitRoomSettings: state.exitRoomSettings,
      raiseHandSettings: state.raiseHandSettings,
      shareScreenSettings: state.shareScreenSettings,
      transcriptionSettings: state.transcriptionSettings,
      whiteboardSettings: state.whiteboardSettings,
      setRaiseHandSettings: state.setRaiseHandSettings,
      screenRecordingSettings: state.screenRecordingSettings,
      setCamSettings: state.setCamSettings,
    }))
  );

  const { peers, isHostUser, viewersCount } = usePeersStore(
    useShallow((state) => ({
      peers: state.peers,
      isHostUser: state.isHostUser,
      viewersCount: state.viewersCount,
    }))
  );

  useBeforeUnload({
    enabled: isScreenRecordingActive && !isCallEnded && isHostUser,
    onBeforeUnload: () => {
      onRecordClick(!isScreenRecordingActive);
    },
  });

  const { setSelectedKey, selectedKey, isDrawerOpen, toggleDrawer } = useDrawerStore(
    useShallow((state) => ({
      selectedKey: state.selectedKey,
      isDrawerOpen: state.isDrawerOpen,
      setSelectedKey: state.setSelectedKey,
      toggleDrawer: state.toggleDrawer,
    }))
  );

  const { questionEnabled } = useWebinarRoomStore(
    useShallow((state) => ({
      questionEnabled: state.attendeeDetails?.roomSetting?.questionSettings?.questionsEnabled,
    }))
  );

  const { hasUnvisitedPolls } = usePollsStore(useShallow((state) => ({ hasUnvisitedPolls: state.hasUnvisitedPolls })));
  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  const { handleCloseWhiteboard, handleOpenWhiteboard, wbCanvasSaveImg } = useWhiteboardContext();
  const { saveEditor } = useNotesContext();
  const { setExitRoomState } = useExitRoomStore();

  const {
    isInfoModalOpen,
    setInfoModalContent,
    setIsInfoModalOpen,
    meetingType,
    isMagicPenAvailable,
    isMagicPenVisible,
    setIsMagicPenVisible,
  } = useRoomStore(
    useShallow((state) => ({
      meetingType: state.meetingType,
      isInfoModalOpen: state.isInfoModalOpen,
      setIsInfoModalOpen: state.setIsInfoModalOpen,
      setInfoModalContent: state.setInfoModalContent,
      isMagicPenAvailable: state.isMagicPenAvailable,
      isMagicPenVisible: state.isMagicPenVisible,
      setIsMagicPenVisible: state.setIsMagicPenVisible,
    }))
  );

  const { userTypeRoomConfig } = useRoomConfigStore(
    useShallow((state) => ({
      userTypeRoomConfig: state.userTypeRoomConfig,
    }))
  );

  const isViewerMode = useMemo(() => !currentPeerType || currentPeerType === EUserType.ATTENDEE, [currentPeerType]);

  const isShareScreenLocked = useMemo(
    () => !!shareScreenStream && !shareScreenStream.isCurrentUser,
    [shareScreenStream]
  );

  // Update countdown every second when trial lock is active
  useEffect(() => {
    if (!shareScreenTrialLockEndTime) {
      setTrialLockCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((shareScreenTrialLockEndTime - now) / 1000));
      setTrialLockCountdown(remaining);

      if (remaining <= 0) {
        setTrialLockCountdown(null);
      }
    };

    // Update immediately
    updateCountdown();

    // Then update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [shareScreenTrialLockEndTime]);

  const shareScreenBtnTooltip = useMemo(() => {
    // If actively sharing, always show stop tooltip regardless of lock
    if (isShareScreenActive) {
      return 'Stop screen sharing';
    }

    // Show countdown if trial lock is active (only when not sharing)
    if (trialLockCountdown !== null && trialLockCountdown > 0) {
      return `You can re-share in ${trialLockCountdown}`;
    }

    if (!isHostOrCohost && isShareScreenLocked && canUserShareScreen) {
      return 'Others are sharing';
    }

    // If loading or has temporary access, show the start/stop tooltip
    if (isShareScreenLoading || hasTempShareAccess) {
      return `${isShareScreenActive ? 'Stop' : 'Start'} screen sharing`;
    }

    if (!canUserShareScreen && !isHostOrCohost && !isCurrentPeerSharing) {
      return 'Ask to share screen';
    }

    return `${isShareScreenActive ? 'Stop' : 'Start'} screen sharing`;
  }, [
    trialLockCountdown,
    isHostOrCohost,
    isShareScreenLocked,
    canUserShareScreen,
    hasTempShareAccess,
    isCurrentPeerSharing,
    isShareScreenActive,
    isShareScreenLoading,
  ]);

  const handleOnClosedCaptionsClicked = useCallback(() => {
    const { id = '' } = getCurrentPeer() || {};
    const isEnabled = !isTranscriptionActive;
    handleUpdatePeerInfo({
      peer_id: id,
      status: isEnabled,
      type: 'speechToText',
      emit: true,
      extraFields: {
        lang: useClosedCaptionsStore.getState().selectedLanguage,
      },
    });
    onTranscriptionClick(isEnabled);
    if (!isEnabled) {
      useClosedCaptionsStore.getState().resetStates();
    }
  }, [getCurrentPeer, handleUpdatePeerInfo, isTranscriptionActive, onTranscriptionClick]);

  const handleAskShareScreen = () => {
    if (canUserShareScreen) {
      return;
    }
    if (hasRequestedShare || isShareScreenLoading) {
      return;
    }
    appendNotification({
      title: '',
      text: `Share screen request sent.`,
      source: 'share-screen',
    });
    SocketService?.emit(ECommonSocketKeys.REQUEST_TO_ADMIT_ACTION, { eventType: 'SHARE_SCREEN' });
    setHasRequestedShare(true);
  };

  const {
    isChatActive,
    isPollsActive,
    isNotesActive,
    isSettingActive,
    isVirtualBackgroundOpen,
    isParticipantsListActive,
    isQuestionAndAnswerActive,
    isCalculatorActive,
  } = useMemo(
    () => ({
      isChatActive: isDrawerOpen && selectedKey === 'chat',
      isNotesActive: isDrawerOpen && selectedKey === 'notes',
      isPollsActive: isDrawerOpen && selectedKey === 'polls',
      isSettingActive: isDrawerOpen && selectedKey === 'settings',
      isQuestionAndAnswerActive: isDrawerOpen && selectedKey === 'question-and-answer',
      isVirtualBackgroundOpen: isDrawerOpen && selectedKey === 'virtual-background',
      isParticipantsListActive: isDrawerOpen && selectedKey === 'participants-list',
      isCalculatorActive: isDrawerOpen && selectedKey === 'calculator',
    }),
    [isDrawerOpen, selectedKey]
  );

  const useEmojiSender = ({
    emitCmd,
    getCurrentPeer,
    handleCmd,
    thereAreParticipants,
  }: {
    emitCmd: (cmd: ISocketCmd) => void;
    getCurrentPeer: () => IPeer | undefined;
    handleCmd: (cmd: ISocketCmd) => void;
    thereAreParticipants: boolean;
  }) => {
    // Map each emoji→array of send‑timestamps
    const emojiTimestampsRef = useRef<Map<TEmojiId, number[]>>(new Map());

    const THROTTLE_WINDOW_MS = 3500;
    const MAX_SENDS_PER_WINDOW = 3;

    const sendEmojiToRoom = useCallback(
      (emojiUnifiedCode: TEmojiId) => {
        const now = Date.now();
        const map = emojiTimestampsRef.current;

        // get or init this emoji's history
        const history = map.get(emojiUnifiedCode) ?? [];

        // drop old timestamps
        const recent = history.filter((ts) => now - ts < THROTTLE_WINDOW_MS);

        // if we've already sent the max for this emoji, bail
        if (recent.length >= MAX_SENDS_PER_WINDOW) {
          return;
        }

        // record this send
        recent.push(now);
        map.set(emojiUnifiedCode, recent);

        // now actually send
        const peer = getCurrentPeer();
        if (!peer) return;

        const cmd: ISocketCmd = {
          type: 'roomEmoji',
          peer_id: peer.id,
          peer_name: peer.peer_name || peer.peer_info.peer_name,
          emoji: emojiUnifiedCode,
          broadcast: true,
        };

        if (thereAreParticipants || viewersCount) {
          emitCmd(cmd);
        }
        handleCmd(cmd);
      },
      [emitCmd, getCurrentPeer, handleCmd, thereAreParticipants]
    );

    return sendEmojiToRoom;
  };

  const sendEmojiToRoom = useEmojiSender({ emitCmd, getCurrentPeer, handleCmd, thereAreParticipants });

  const handleEmojiClick = useCallback(
    (emojiName: TEmojiId) => {
      sendEmojiToRoom(emojiName);
    },
    [sendEmojiToRoom]
  );

  const handleEndCallClick = useCallback(() => {
    setIsCallEnded(true);
    const peerCount = peers.size ?? 1;

    if (peerCount === 1) {
      onExitClick(true);
      return;
    }
    if (!canUserEndMeeting) {
      onExitClick();
      return;
    }
    toggleDrawer(false);
    setExitRoomState({ isOpenModal: true });
  }, [setIsCallEnded, peers.size, canUserEndMeeting, setExitRoomState, onExitClick, toggleDrawer]);

  const handleSaveNote = useCallback(() => {
    wbCanvasSaveImg();
    saveEditor();
  }, [wbCanvasSaveImg, saveEditor]);

  const { isAudioAllowed, isVideoAllowed } = useMediaSettingsStore(
    useShallow((state) => ({
      isAudioAllowed: state.mediaSettings.isAudioAllowed,
      isVideoAllowed: state.mediaSettings.isVideoAllowed,
    }))
  );

  const { initEnumerateAudioDevices, initEnumerateVideoDevices } = useEnumerateDevices();

  const handleMicClick = useCallback(async () => {
    if (!isAudioAllowed) {
      if (!isIOS() && !isWebView()) {
        setInfoModalContent({
          icon: INFO_MODAL_ICONS.ACCESS_MIC,
          title: 'Microphone Access Denied',
          description: 'Please allow Microphone access from your browser settings to continue.',
        });

        setIsInfoModalOpen(true);
      } else {
        initEnumerateAudioDevices();
      }
      return;
    }

    if (isMicClosed && micRequest) {
      setMicRequest(null);
    }

    onMicClick(!isMicClosed);

    if (!isMicClosed) {
      setIsMicExtensionOpen(false);
    }
  }, [
    initEnumerateAudioDevices,
    isAudioAllowed,
    isMicClosed,
    micRequest,
    onMicClick,
    setInfoModalContent,
    setIsInfoModalOpen,
    setMicRequest,
  ]);

  const handleCamClick = useMemo(() => {
    return async () => {
      if (!isVideoAllowed) {
        if (!isIOS() && !isWebView()) {
          setInfoModalContent({
            icon: INFO_MODAL_ICONS.ACCESS_CAMERA,
            title: 'Camera Access Denied',
            description: 'Please allow Camera access from your browser settings to start the video.',
          });
          setIsInfoModalOpen(true);
        } else {
          initEnumerateVideoDevices();
        }
        return;
      }

      try {
        if (!isCamClosed) {
          await onCamClick(true); // Close camera first
          setIsVideoExtensionOpen(false);
        } else {
          if (camRequest) {
            setCamRequest(null);
          }
          const isSamsung = isSamsungDevice();

          if (isSamsung) {
            setCamSettings({ isCamClosed: false });
          }
          await onCamClick(false); // Then open camera
        }
      } catch (error) {
        console.error('Error handling camera toggle:', error);
        // Show error to user
        setInfoModalContent({
          icon: INFO_MODAL_ICONS.ACCESS_CAMERA,
          title: 'Camera Error',
          description: 'Failed to toggle camera. Please try again.',
        });
        setIsInfoModalOpen(true);
      }
    };
  }, [
    isVideoAllowed,
    setInfoModalContent,
    setIsInfoModalOpen,
    initEnumerateVideoDevices,
    isCamClosed,
    onCamClick,
    camRequest,
    setCamRequest,
    setCamSettings,
  ]);

  return {
    isHostUser,
    isMicClosed,
    isMicButtonDisabled,
    onMicClick,
    handleMicClick,

    isCamClosed,
    isCamButtonDisabled,
    onCamClick,
    handleCamClick,

    isInfoModalOpen,

    isRaiseHandActive,
    isRaiseHandDisabled,
    onRaiseHandClick,

    isShareScreenActive,
    isShareScreenDisabled,
    isShareScreenLoading,
    isCurrentPeerSharing,
    onShareScreenClick,
    trialLockCountdown,

    isTranscriptionActive,
    isTranscriptionDisabled,
    handleOnClosedCaptionsClicked,

    whiteboardSettings,
    handleOpenWhiteboard,
    handleCloseWhiteboard,
    handleSaveNote,
    setSelectedKey,
    handleEmojiClick,
    handleAskShareScreen,

    isPublicChatEnabled,
    isPrivateChatEnabled,
    isQuestionAndAnswerEnabled,
    isPollEnabled,
    canUserShareScreen,
    userTypeRoomConfig,

    participantsPeers,
    isHostOrCohost,
    isChatActive,
    hasChatNotifications,
    isPollsActive,
    isNotesActive,
    hasUnvisitedPolls,
    isShareScreenLocked,
    shareScreenBtnTooltip,
    hasActiveLimitedPolls,
    isParticipantsListActive,
    isQuestionAndAnswerActive,
    isCalculatorActive,

    shareScreenStream,
    isVirtualBackgroundOpen,
    isSettingActive,
    handleEndCallClick,

    hasRequestedShare,
    hasTempShareAccess,
    setHasUsedShareAccess,

    isMicExtensionOpen,
    setIsMicExtensionOpen,
    isVideoExtensionOpen,
    setIsVideoExtensionOpen,
    speakerDevices,
    audioInputDevices,
    selectedAudio,
    selectedSpeaker,
    videoInputDevices,
    selectedCamera,
    handleChangeMediaDevice,
    micRequest,
    setMicRequest,
    camRequest,
    setCamRequest,
    micButtonRef,
    camButtonRef,

    isAudioAllowed,
    isVideoAllowed,

    // buttons visibility
    isStartAudioButtonEnabled,
    isStartVideoButtonEnabled,
    isTranscriptionButtonEnabled,
    isEmojiRoomButtonEnabled,
    isShareButtonEnabled,
    isRaiseHandButtonEnabled,
    isExitButtonEnabled,
    isEditorButtonEnabled,
    isParticipantsButtonEnabled,
    isThreeDotsFooterMenuEnabled,
    currentPeerId,
    currentPeerType,
    meetingType,
    currentPeer,
    viewersCount,
    questionEnabled,

    // Magic Pen
    isMagicPenAvailable,
    isMagicPenVisible,
    setIsMagicPenVisible,
    isViewerMode,
  };
};

export default useRoomFooter;
